import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';
import {
    Cell,
    Pie,
    PieChart,
    ResponsiveContainer,
} from 'recharts';

const LAB_COLORS = [
    '#04c2ff',
    '#0288d1',
    '#1e40af',
    '#4f46e5',
    '#2563eb',
    '#0f766e',
    '#0369a1',
    '#4338ca',
    '#1d4ed8',
    '#312e81',
];
const OTHER_COLOR = '#d1d5db';
const INACTIVE_COLOR = '#e5e7eb';

const normalizeLabName = (labName) => {
    const trimmedName = labName?.trim();

    return trimmedName || 'Unknown Lab';
};

const buildChartRows = (rows, totalSubmittedCases) => {
    const aggregatedRows = rows.reduce((acc, row) => {
        const label = normalizeLabName(row.lab_name);
        const nextCount = (acc[label]?.caseCount || 0) + (row.case_count || 0);

        return {
            ...acc,
            [label]: {
                caseCount: nextCount,
                label,
            },
        };
    }, {});

    const sortedRows = Object.values(aggregatedRows)
        .sort((left, right) => right.caseCount - left.caseCount);
    const rankedCaseCount = sortedRows.reduce((acc, row) => acc + row.caseCount, 0);
    const totalCases = Math.max(totalSubmittedCases || 0, rankedCaseCount);
    const otherCaseCount = Math.max(totalCases - rankedCaseCount, 0);

    const baseRows = sortedRows.map((row, index) => ({
        ...row,
        color: LAB_COLORS[index % LAB_COLORS.length],
        percentage: totalCases > 0 ? (row.caseCount / totalCases) * 100 : 0,
    }));

    if (otherCaseCount > 0) {
        baseRows.push({
            caseCount: otherCaseCount,
            color: OTHER_COLOR,
            label: 'Other',
            percentage: totalCases > 0 ? (otherCaseCount / totalCases) * 100 : 0,
        });
    }

    return {
        chartRows: baseRows,
        totalCases,
    };
};

const formatCaseCount = (value) => value.toLocaleString();
const formatPercentage = (value) => `${value.toFixed(1)}%`;
const getDisplayColor = (row, activeLabel) => {
    if (!activeLabel || row.label === activeLabel) {
        return row.color;
    }

    return INACTIVE_COLOR;
};

const getTotalSubmittedCases = (analytics) => (analytics?.rows || []).reduce(
    (acc, row) => acc + (row.case_count || 0),
    0,
);

function LabDestinationVolumeChart({ analytics, totalAnalytics }) {
    const [activeLabel, setActiveLabel] = useState('');
    const rows = analytics?.rows || [];
    const totalSubmittedCases = getTotalSubmittedCases(totalAnalytics);
    const { chartRows, totalCases } = buildChartRows(rows, totalSubmittedCases);
    const hasRows = totalCases > 0;

    return (
        <section className="dashboard-chart-card">
            <div className="dashboard-chart-header dashboard-chart-header--with-icon">
                <h2>Lab Destination Volume</h2>
                <Tooltip title="Scan volume distribution across destination labs for the selected date range.">
                    <InfoOutlinedIcon className="dashboard-chart-info-icon" fontSize="small" />
                </Tooltip>
            </div>
            <div className={`dashboard-chart-body${hasRows ? ' dashboard-chart-body--auto' : ''}`}>
                {hasRows ? (
                    <div className="dashboard-lab-chart">
                        <div className="dashboard-lab-chart__visual">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={chartRows}
                                        dataKey="caseCount"
                                        nameKey="label"
                                        innerRadius={70}
                                        outerRadius={126}
                                        startAngle={90}
                                        endAngle={-270}
                                        stroke="#ffffff"
                                        strokeWidth={3}
                                        onMouseLeave={() => setActiveLabel('')}
                                    >
                                        {chartRows.map((row) => (
                                            <Cell
                                                key={row.label}
                                                fill={getDisplayColor(row, activeLabel)}
                                                fillOpacity={!activeLabel || row.label === activeLabel ? 1 : 0.7}
                                                onMouseEnter={() => setActiveLabel(row.label)}
                                            />
                                        ))}
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="dashboard-lab-chart__total">
                                <div className="dashboard-lab-chart__total-value">
                                    {formatCaseCount(totalCases)}
                                </div>
                                <div className="dashboard-lab-chart__total-label">Total</div>
                            </div>
                        </div>
                        <div className="dashboard-lab-chart__legend">
                            {chartRows.map((row) => (
                                <div
                                    key={row.label}
                                    className={`dashboard-lab-chart__legend-row${
                                        activeLabel && row.label !== activeLabel
                                            ? ' dashboard-lab-chart__legend-row--inactive'
                                            : ''
                                    }`}
                                    onMouseEnter={() => setActiveLabel(row.label)}
                                    onMouseLeave={() => setActiveLabel('')}
                                >
                                    <div className="dashboard-lab-chart__legend-label">
                                        <span
                                            className="dashboard-lab-chart__legend-dot"
                                            style={{ backgroundColor: getDisplayColor(row, activeLabel) }}
                                        />
                                        <span>{row.label}</span>
                                    </div>
                                    <div className="dashboard-lab-chart__legend-value">
                                        {formatCaseCount(row.caseCount)}
                                    </div>
                                    <div className="dashboard-lab-chart__legend-percentage">
                                        {formatPercentage(row.percentage)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="dashboard-chart-empty">
                        <p>No data for the selected date range.</p>
                        <p>Try expanding the date range or adjusting filters.</p>
                    </div>
                )}
            </div>
        </section>
    );
}

LabDestinationVolumeChart.propTypes = {
    analytics: PropTypes.shape({
        rows: PropTypes.arrayOf(PropTypes.shape({
            case_count: PropTypes.number,
            lab_id: PropTypes.string,
            lab_name: PropTypes.string,
        })),
    }),
    totalAnalytics: PropTypes.shape({
        rows: PropTypes.arrayOf(PropTypes.shape({
            case_count: PropTypes.number,
        })),
    }),
};

LabDestinationVolumeChart.defaultProps = {
    analytics: null,
    totalAnalytics: null,
};

export default LabDestinationVolumeChart;
